/**
 * Aba Repositórios: as fontes do KB-Git, uma por repositório, com o commit que
 * gerou o dossiê (spec 2026-09-26 KB-Git, §11). `audit` lê; `manage` exclui,
 * reativa e regera. O gate real é do backend — a tela só não oferece o que ele
 * recusaria (`reativar` só de `excluido`, `regerar` só fora dele).
 *
 * Fonte `bloqueada` (§16): falhou no teto de tentativas e a passada não a coleta
 * mais. A tela diz isso, o motivo em português e o que fazer — sem isso ela ficava
 * "Falhou" com o código cru, e ninguém sabia que tinha parado de vez.
 */
import { ActionIcon, Alert, Anchor, Badge, Menu, Skeleton, Stack, Table, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { IconAlertTriangle, IconBrandGit, IconDotsVertical, IconPlayerPlay, IconRefresh, IconTrash } from '@tabler/icons-react'
import { listKbGitSources, mudarKbGitSource, type KbGitSource, type KbGitSourceEstado, type KbGitSourceOperacao } from '@/api/kb'
import { EmptyState } from '@/components/ui/EmptyState'
import { mensagemDeErro } from './acesso'
import { formatarData, shaCurto } from './formato'

const ESTADO_META: Record<KbGitSourceEstado, { label: string; color: string }> = {
  pendente: { label: 'Pendente', color: 'gray' },
  atual: { label: 'Atual', color: 'teal' },
  falhou: { label: 'Falhou', color: 'red' },
  inativo: { label: 'Inativo', color: 'yellow' },
  pausado: { label: 'Pausado', color: 'yellow' },
  excluido: { label: 'Excluído', color: 'gray' },
}

/** O código de `erro` da fonte em português. O código cru segue na tela, para suporte. */
const MOTIVO: Record<string, string> = {
  // `coleta._get` lê todo 404 como projeto inexistente, e o GitLab responde 404 também
  // a quem não tem acesso ao projeto: daqui não há como separar os dois casos.
  projeto_inexistente:
    'O GitLab respondeu 404 para este projeto: ou ele foi apagado, ou o token do ConsoleAI não tem acesso a ele. O GitLab responde igual nos dois casos, então não dá para saber qual daqui.',
  branch_inexistente: 'Nem a branch configurada no tenant nem a branch padrão do projeto existem no GitLab.',
  acesso_negado: 'O GitLab recusou o token do ConsoleAI (401/403).',
  data_de_commit_invalida: 'O último commit da branch veio do GitLab sem data válida.',
  resposta_vazia: 'O modelo devolveu uma resposta vazia ao gerar o dossiê.',
  citacoes_invalidas: 'O dossiê gerado citava arquivos que não existem no repositório e foi descartado.',
}

export function motivoDaFalha(erro: string): string {
  if (MOTIVO[erro]) return MOTIVO[erro]
  if (erro.startsWith('erro:')) return `Erro inesperado ao gerar o dossiê (${erro.slice(5)}).`
  return erro
}

function oQueFazer(f: KbGitSource, gere: boolean): string {
  const parou = `A passada diária parou de tentar depois de ${f.tentativas} falhas seguidas.`
  if (!gere) return `${parou} Quem gere a Base de Conhecimento pode regerar ou excluir o repositório.`
  if (f.erro === 'projeto_inexistente') {
    return `${parou} Se o projeto foi apagado, exclua o repositório; se ele existe, dê acesso ao token do ConsoleAI e use "Regerar na próxima passada".`
  }
  return `${parou} Corrija a causa e use "Regerar na próxima passada", ou exclua o repositório.`
}

const SUCESSO: Record<KbGitSourceOperacao, string> = {
  excluir: 'Repositório excluído',
  reativar: 'Repositório reativado',
  regerar: 'Regeração pedida para a próxima passada',
}

function nomeDe(f: KbGitSource): string {
  return f.caminho ?? `Projeto ${f.gitlab_project_id}`
}

interface Props {
  /** `assistant_kb:manage` */
  gere: boolean
  nomesDeOrg: Map<number, string>
}

export function Repositorios({ gere, nomesDeOrg }: Props) {
  const qc = useQueryClient()
  const { data: fontes = [], isLoading, error } = useQuery({ queryKey: ['kb-git-sources'], queryFn: () => listKbGitSources() })

  const mudar = useMutation({
    mutationFn: ({ f, op }: { f: KbGitSource; op: KbGitSourceOperacao }) => mudarKbGitSource(f.id, op),
    onSuccess: (_r, { f, op }) => {
      qc.invalidateQueries({ queryKey: ['kb-git-sources'] })
      qc.invalidateQueries({ queryKey: ['kb-documents'] })
      notifications.show({ color: 'teal', title: SUCESSO[op], message: nomeDe(f) })
    },
    onError: (e: unknown) =>
      notifications.show({ color: 'red', title: 'Não foi possível concluir', message: mensagemDeErro(e, 'Falha na operação.') }),
  })

  const confirmarExclusao = (f: KbGitSource) =>
    modals.openConfirmModal({
      title: 'Excluir repositório',
      children: (
        <Text size="sm">
          Excluir <strong>{nomeDe(f)}</strong> da Base de Conhecimento? A passada deixa de gerar o dossiê; o documento
          fica, e pode ser apagado depois.
        </Text>
      ),
      labels: { confirm: 'Excluir fonte', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => mudar.mutate({ f, op: 'excluir' }),
    })

  if (isLoading) return <Skeleton height={160} />

  // Erro não é "sem repositório": diria que não há fonte a quem só falta permissão.
  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Não foi possível carregar os repositórios">
        {mensagemDeErro(error, 'Tente de novo em instantes.')}
      </Alert>
    )
  }

  if (fontes.length === 0) {
    return (
      <EmptyState
        icon={IconBrandGit}
        title="Nenhum repositório ainda"
        description="Os repositórios aparecem aqui depois da passada diária do KB-Git, para os tenants com Terraform e Git configurados."
      />
    )
  }

  const bloqueadas = fontes.filter((f) => f.bloqueada).length

  return (
    <Stack gap="sm">
      {bloqueadas > 0 && (
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={16} />}
          title={bloqueadas === 1 ? '1 repositório bloqueado' : `${bloqueadas} repositórios bloqueados`}
        >
          A passada diária não tenta mais gerar o dossiê destes repositórios até alguém regerar ou excluir. O motivo
          está na linha de cada um.
        </Alert>
      )}
      <Table verticalSpacing="xs" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Repositório</Table.Th>
            <Table.Th>Organização</Table.Th>
            <Table.Th>Branch</Table.Th>
            <Table.Th>Commit</Table.Th>
            <Table.Th>Estado</Table.Th>
            <Table.Th>Gerado em</Table.Th>
            {gere && <Table.Th aria-label="Ações" />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {fontes.map((f) => {
            const meta = f.bloqueada
              ? { label: 'Bloqueada', color: 'orange' }
              : (ESTADO_META[f.estado] ?? { label: f.estado, color: 'gray' })
            const excluida = f.estado === 'excluido'
            return (
              <Table.Tr key={f.id}>
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="sm" fw={600}>{nomeDe(f)}</Text>
                    {f.estado === 'falhou' && f.erro && (
                      <>
                        <Text size="xs" c="red">{motivoDaFalha(f.erro)}</Text>
                        <Text size="xs" c="dimmed" ff="monospace">{f.erro}</Text>
                      </>
                    )}
                    {f.bloqueada && <Text size="xs" c="orange">{oQueFazer(f, gere)}</Text>}
                  </Stack>
                </Table.Td>
                <Table.Td><Text size="sm">{nomesDeOrg.get(f.organization_id) ?? `Organização ${f.organization_id}`}</Text></Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{f.branch}</Text></Table.Td>
                <Table.Td>
                  {f.ultimo_commit ? (
                    f.commit_url ? (
                      <Anchor href={f.commit_url} target="_blank" rel="noopener noreferrer" size="xs" ff="monospace">
                        {shaCurto(f.ultimo_commit)}
                      </Anchor>
                    ) : (
                      <Text size="xs" ff="monospace">{shaCurto(f.ultimo_commit)}</Text>
                    )
                  ) : (
                    <Text size="xs" c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td><Badge variant="light" color={meta.color}>{meta.label}</Badge></Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace">{f.gerado_em ? formatarData(f.gerado_em) : '—'}</Text>
                </Table.Td>
                {gere && (
                  <Table.Td>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label={`Ações de ${nomeDe(f)}`}>
                          <IconDotsVertical size={16} stroke={1.5} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {excluida ? (
                          <Menu.Item leftSection={<IconPlayerPlay size={14} />} onClick={() => mudar.mutate({ f, op: 'reativar' })}>
                            Reativar
                          </Menu.Item>
                        ) : (
                          <>
                            <Menu.Item leftSection={<IconRefresh size={14} />} onClick={() => mudar.mutate({ f, op: 'regerar' })}>
                              Regerar na próxima passada
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => confirmarExclusao(f)}>
                              Excluir
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                )}
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  )
}
